import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { stripHtml } from './richText';

export const SITE_NAME = 'Automeet Network';

const DEFAULT_DESC = {
  ar: 'Automeet Network منصة تعليمية عربية لتعلم الأتمتة الصناعية وبرمجة PLC وSCADA وHMI من الصفر إلى الاحتراف، بكورسات عملية ومقالات وفيديوهات واختبارات.',
  en: 'Automeet Network is an Arabic learning platform for industrial automation, PLC, SCADA and HMI programming — from zero to pro with practical courses, articles, videos and interactive simulations.',
};

// Static pages: [path pattern, Arabic title, English title, Arabic description, English description]
const ROUTES = [
  [/^\/$/, '', '', DEFAULT_DESC.ar, DEFAULT_DESC.en],
  [/^\/courses\/?$/, 'الكورسات', 'Courses', 'تصفح كورسات الأتمتة الصناعية وبرمجة PLC وSCADA على Automeet Network، مع دروس فيديو عملية ومحتوى مجاني ومدفوع.', 'Browse industrial automation, PLC and SCADA courses on Automeet Network, with practical video lessons and free and premium content.'],
  [/^\/courses\/[^/]+\/learn\/?$/, 'مشاهدة الدرس', 'Watch lesson', '', ''],
  [/^\/courses\/[^/]+\/?$/, 'تفاصيل الكورس', 'Course details', '', ''],
  [/^\/library\/post\/[^/]+\/?$/, 'مقال', 'Article', '', ''],
  [/^\/library\/video\/[^/]+\/?$/, 'فيديو', 'Video', '', ''],
  [/^\/library\/?$/, 'المكتبة', 'Library', 'مكتبة Automeet Network: مقالات وفيديوهات ومحاكاة تفاعلية في الأتمتة الصناعية والبرمجة وأنظمة التحكم.', 'The Automeet Network library: articles, videos and simulations on industrial automation, programming and control systems.'],
  [/^\/articles\/?$/, 'المقالات', 'Articles', 'مقالات تقنية مبسطة عن الأتمتة الصناعية وPLC وSCADA والشبكات الصناعية.', 'Clear technical articles on industrial automation, PLC, SCADA and industrial networks.'],
  [/^\/videos\/?$/, 'الفيديوهات', 'Videos', 'فيديوهات تعليمية عملية في الأتمتة الصناعية وبرمجة PLC وأنظمة التحكم.', 'Practical tutorial videos on industrial automation, PLC programming and control systems.'],
  [/^\/simulations\/[^/]+\/?$/, 'محاكاة تفاعلية', 'Interactive simulation', '', ''],
  [/^\/simulations\/?$/, 'المحاكاة التفاعلية', 'Interactive Simulations', 'تعلّم الأتمتة والتحكم الصناعي عبر محاكاة تفاعلية لـ PLC والمحركات والحساسات والدارات الكهربائية.', 'Learn industrial automation through interactive simulations of PLCs, motors, sensors and electrical circuits.'],
  [/^\/subscription\/?$/, 'الاشتراك', 'Subscription', 'اشترك في Automeet Network واحصل على وصول كامل لكل الكورسات والفيديوهات والمحتوى المدفوع.', 'Subscribe to Automeet Network for full access to every course, video and premium resource.'],
  [/^\/payment\/success\/?$/, 'تأكيد الدفع', 'Payment confirmation', '', ''],
  [/^\/payment\/cancel\/?$/, 'تم إلغاء الدفع', 'Payment cancelled', '', ''],
  [/^\/about\/?$/, 'من نحن', 'About us', 'تعرّف على Automeet Network، المنصة العربية المتخصصة في تعليم الأتمتة الصناعية والبرمجة وأنظمة التحكم.', 'Meet Automeet Network, the Arabic platform dedicated to teaching industrial automation, programming and control systems.'],
  [/^\/contact\/?$/, 'تواصل معنا', 'Contact us', 'تواصل مع فريق Automeet Network لأي استفسار عن الكورسات أو الاشتراكات أو الدعم الفني.', 'Contact the Automeet Network team about courses, subscriptions or technical support.'],
  [/^\/login\/?$/, 'تسجيل الدخول', 'Log in', 'سجّل الدخول إلى حسابك في Automeet Network لمتابعة تعلمك.', 'Log in to your Automeet Network account to continue learning.'],
  [/^\/auth\/google\/?$/, 'تسجيل الدخول', 'Log in', '', ''],
  [/^\/register\/?$/, 'إنشاء حساب', 'Sign up', 'أنشئ حساباً مجانياً في Automeet Network وابدأ تعلم الأتمتة الصناعية.', 'Create a free Automeet Network account and start learning industrial automation.'],
  [/^\/profile\/?$/, 'حسابي', 'My profile', '', ''],
  [/^\/admin\/courses\/new\/?$/, 'كورس جديد', 'New course', '', ''],
  [/^\/admin\/courses\/[^/]+\/?$/, 'تعديل الكورس', 'Edit course', '', ''],
  [/^\/admin\/?$/, 'لوحة التحكم', 'Dashboard', '', ''],
];

let override = null;

const setDescription = (text) => {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) { tag = document.createElement('meta'); tag.name = 'description'; document.head.appendChild(tag); }
  tag.content = text;
};

const apply = (pathname, lang) => {
  const ar = lang === 'ar';
  const m = override && override.path === pathname ? override : null;
  const route = ROUTES.find(([re]) => re.test(pathname));
  const page = m?.title || (route ? (ar ? route[1] : route[2]) : (ar ? 'الصفحة غير موجودة' : 'Page not found'));
  document.title = page ? `${page} | ${SITE_NAME}` : SITE_NAME;
  setDescription(m?.description || (route && (ar ? route[3] : route[4])) || DEFAULT_DESC[ar ? 'ar' : 'en']);
};

const clip = (s, n = 160) => {
  const t = stripHtml(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1).trim()}…` : t;
};

// Mount once inside the router: sets the title on every navigation and language change.
export const SeoManager = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();
  useEffect(() => { apply(pathname, lang); }, [pathname, lang]);
  return null;
};

// Dynamic pages call this with the real item name once it has loaded.
export const usePageMeta = (title, description) => {
  const { pathname } = useLocation();
  const { lang } = useLang();
  useEffect(() => {
    override = title ? { path: pathname, title, description: clip(description) } : null;
    apply(pathname, lang);
    return () => { if (override?.path === pathname) override = null; };
  }, [pathname, lang, title, description]);
};
